import { useEffect, useCallback } from 'preact/hooks';
import { isOpen, token, conversationId, error as errorSignal } from '../store/state';
import { WidgetApiClient } from '../api/client';
import { TokenManager } from '../api/token';
import { ChatLauncher } from './ChatLauncher';
import { ChatPanel } from './ChatPanel';
import type { WidgetConfig } from '../types';

interface WidgetProps {
  config: WidgetConfig;
}

// Singleton instances
let apiClient: WidgetApiClient | null = null;
let tokenManager: TokenManager | null = null;

export function Widget({ config }: WidgetProps) {
  const position = config.position || 'bottom-right';

  // Initialize API client and token manager
  useEffect(() => {
    apiClient = new WidgetApiClient(config.apiUrl);
    tokenManager = new TokenManager(config);
    apiClient.setTokenManager(tokenManager);

    // Set up auth error handler - purge token and re-authenticate
    // Keep conversationId so the conversation can be resumed after re-auth
    const handleAuthError = () => {
      tokenManager?.handleAuthError();
      token.value = null;
      errorSignal.value = 'Session expired. Reconnecting...';

      // Re-fetch token if widget is open
      if (isOpen.value && tokenManager) {
        const tokenPromise = config.usePublicToken
          ? tokenManager.fetchPublicToken()
          : tokenManager.fetchToken(config.tokenEndpoint || `${window.location.origin}/api/easyhelp/token`);

        tokenPromise.then(() => {
          errorSignal.value = null; // Clear error on success
        }).catch((err) => {
          errorSignal.value = err instanceof Error ? err.message : 'Failed to reconnect';
        });
      }
    };

    apiClient.setAuthErrorCallback(handleAuthError);

    // Set up token refresh callback
    tokenManager.setRefreshCallback((newToken, newConversationId) => {
      token.value = newToken;
      conversationId.value = newConversationId;
      apiClient?.setToken(newToken);
      errorSignal.value = null; // Clear any reconnection error
    });

    // Check for existing valid token
    const existingToken = tokenManager.getToken();
    if (existingToken) {
      token.value = existingToken;
      conversationId.value = tokenManager.getConversationId();
      apiClient.setToken(existingToken);
    }

    return () => {
      tokenManager?.destroy();
    };
  }, [config]);

  // Listen for external open/close events
  useEffect(() => {
    const container = document.getElementById('easyhelp-widget-container');
    if (!container) return;

    const handleOpen = () => { isOpen.value = true; };
    const handleClose = () => { isOpen.value = false; };
    const handleToggle = () => { isOpen.value = !isOpen.value; };

    container.addEventListener('easyhelp:open', handleOpen);
    container.addEventListener('easyhelp:close', handleClose);
    container.addEventListener('easyhelp:toggle', handleToggle);

    return () => {
      container.removeEventListener('easyhelp:open', handleOpen);
      container.removeEventListener('easyhelp:close', handleClose);
      container.removeEventListener('easyhelp:toggle', handleToggle);
    };
  }, []);

  // Fetch token when widget opens (if no token, or token exists but no conversation)
  useEffect(() => {
    if (!isOpen.value) return;
    if (token.value && conversationId.value) return;

    const fetchToken = async () => {
      try {
        if (tokenManager) {
          if (config.usePublicToken) {
            // Use EasyHelp's public token endpoint directly
            await tokenManager.fetchPublicToken();
          } else {
            // Use host app's token endpoint
            const hostTokenEndpoint = config.tokenEndpoint || `${window.location.origin}/api/easyhelp/token`;
            await tokenManager.fetchToken(hostTokenEndpoint);
          }
        }
      } catch (err) {
        errorSignal.value = err instanceof Error ? err.message : 'Failed to connect';
      }
    };

    fetchToken();
  }, [isOpen.value, config.tokenEndpoint, config.usePublicToken]);

  const handleToggle = useCallback(() => {
    isOpen.value = !isOpen.value;
  }, []);

  return (
    <div class={`easyhelp-widget position-${position}`}>
      <ChatPanel
        config={config}
        apiClient={apiClient}
        isOpen={isOpen.value}
      />
      <ChatLauncher
        isOpen={isOpen.value}
        onClick={handleToggle}
      />
    </div>
  );
}

// Export singleton getters for use in other components
export function getApiClient(): WidgetApiClient | null {
  return apiClient;
}

export function getTokenManager(): TokenManager | null {
  return tokenManager;
}
